import { manageNavItems } from "@/config/navigation";
import { RoutePaths } from "@/config/route-paths";
import { useIsMobile } from "@cmde/hooks";
import {
  Button,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Sheet,
  SheetClose,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@cmde/shadcn";
import { HomeIcon, MenuIcon, Settings2Icon, type LucideIcon } from "lucide-react";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router";

function MobileMenuItem({
  title,
  href,
  icon: Icon,
  description,
  onSelect,
}: {
  title: string;
  href: string;
  icon?: LucideIcon;
  description?: string;
  onSelect?: () => void;
}) {
  return (
    <SheetClose asChild>
      <Button variant="ghost" size="lg" className="w-full justify-start rounded-md" asChild>
        <Link to={href} onClick={onSelect}>
          {Icon ? (
            <div className="mt-0.5">
              <Icon className="size-5 text-muted-foreground" />
            </div>
          ) : null}
          <div className="flex flex-col gap-1">
            <span className="text-sm font-medium">{title}</span>
            {description ? <span className="text-xs text-muted-foreground">{description}</span> : null}
          </div>
        </Link>
      </Button>
    </SheetClose>
  );
}

export function HeaderMenu() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [sheetOpen, setSheetOpen] = useState(false);
  const { pathname } = useLocation();
  const { customerId, organizationId, sport } = useParams();

  const params = useMemo(() => {
    if (!customerId || !organizationId || !sport) {
      return null;
    }

    return {
      customerId: Number(customerId),
      organizationId,
      sport,
    };
  }, [customerId, organizationId, sport]);

  const menuItems = useMemo(() => {
    if (!params) {
      return [];
    }

    return manageNavItems.map((item) => ({
      title: t(item.labelKey),
      description: item.descriptionKey ? t(item.descriptionKey) : undefined,
      href: item.href(params),
      icon: item.icon,
      isActive: item.isActive(pathname),
    }));
  }, [params, pathname, t]);

  if (!params) {
    return null;
  }

  const homeHref = RoutePaths.organizations.list({ customerId: params.customerId });

  if (isMobile) {
    return (
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetTrigger asChild>
          <Button variant="ghost" size="icon-lg" className="md:hidden">
            <MenuIcon className="size-5" />
            <span className="sr-only">{t("common.openMenu")}</span>
          </Button>
        </SheetTrigger>
        <SheetContent side="right" className="w-75 sm:w-87.5 px-3">
          <SheetHeader>
            <SheetTitle className="sr-only">{t("headerMessages.menu.title")}</SheetTitle>
          </SheetHeader>
          <nav className="mt-4 flex flex-col gap-2">
            <MobileMenuItem
              title={t("headerMessages.menu.home.title")}
              href={homeHref}
              icon={HomeIcon}
              onSelect={() => setSheetOpen(false)}
            />

            <div className="my-2 border-t" />

            <div className="px-3 py-2">
              <span className="font-brand text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t("headerMessages.menu.manage.title")}
              </span>
            </div>

            {menuItems.map((item) => (
              <MobileMenuItem
                key={item.href}
                title={item.title}
                href={item.href}
                icon={item.icon}
                description={item.description}
                onSelect={() => setSheetOpen(false)}
              />
            ))}
          </nav>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div className="flex grow items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={cn(
              "group font-brand",
              "hover:bg-primary/5! text-secondary hover:dark:bg-accent/50! [&_svg]:text-accent",
            )}
          >
            <Settings2Icon className="size-4 group-hover:stroke-accent" />
            {t("headerMessages.menu.manage.title")}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="mt-2 w-72 p-2" align="end">
          <DropdownMenuLabel className="px-2 pb-2 pt-1 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
            {t("headerMessages.menu.manage.title")}
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="mb-1" />
          {menuItems.map((item) => (
            <DropdownMenuItem
              key={item.href}
              asChild
              className={cn(
                "group cursor-pointer items-start gap-3 rounded-none px-3 py-3 border-l-3 border-transparent",
                item.isActive && "bg-primary/5 text-primary border-l-accent",
              )}
            >
              <Link to={item.href}>
                <div
                  className={cn(
                    "flex size-8 shrink-0 items-center justify-center rounded-md transition-colors",
                    "bg-muted group-focus:bg-primary/5",
                  )}
                >
                  <item.icon className="size-4 transition-colors text-accent" />
                </div>
                <div className="flex flex-col gap-1">
                  <span className="text-sm font-semibold leading-none">{item.title}</span>
                  {item.description ? (
                    <span className="text-xs font-normal leading-snug text-muted-foreground!">{item.description}</span>
                  ) : null}
                </div>
              </Link>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
